import { useState, useRef } from 'react';
import { createInsiderProgramV4Service, CurationPromptV2Service } from '../../../../services/learning_programs.service';
import { CHAT_TYPE } from '../../../../utils/constants';

/**
 * LearnCreator program-creation chat, backed by the DQ AI chatbot.
 *
 * One request per turn, one JSON reply. The server runs the interview, owns the wizard
 * params and decides when a card belongs on screen; this hook only turns that reply into
 * chat bubbles and hands the build over to the polling layer. It replaces the SSE hook
 * that had to reconstruct wizard state by parsing {{marker}} tokens out of a token stream.
 *
 * @param {Function} handleBuildProgram polling-only build callback, called with the program
 *                                      code once the server reports the build has started
 * @param {Function} setWizard          wizard state setter
 */
export const useDqAiProgramChat = (handleBuildProgram, setWizard) => {
    const [chatHistory, setChatHistory] = useState([]);
    // The server names the step it is on ('area', 'audience', … 'alignment',
    // … 'summary'); see logic.currentStage on the API side for the sequence.
    const [curationStage, setCurationStage] = useState('');
    const [isBotThinking, setIsBotThinking] = useState(false);
    const [programIdState, setProgramIdState] = useState(null);
    const [insufficientCredits, setInsufficientCredits] = useState(false);

    const buildTriggeredRef       = useRef(false);
    /*
     * The same fact as buildTriggeredRef, held as STATE so the UI re-renders
     * on it. It answers "has this program been committed to a build", which
     * stays true after the build finishes, fails, or is abandoned — unlike
     * isProgramBuilding, which is only true while the poller runs.
     */
    const [buildStarted, setBuildStarted] = useState(false);
    const serverBuildInitiatedRef = useRef(false);
    const serverProgramCodeRef    = useRef(null);
    const insufficientCreditsRef  = useRef(false);
    const programBuildFailedRef   = useRef(false);
    const confirmBuildCostRef     = useRef(null);
    const inFlightRef             = useRef(false);
    // Set by "No, make changes": the reply to that turn asks what should change, so the
    // cost card the server attaches to it would only repeat the decision just declined.
    const dropCostCardOnceRef     = useRef(false);
    // The first message of a chat that could not start for want of credits — sent again
    // once the credits are there (Try again, or a successful top-up).
    const pendingStartRef         = useRef(null);

    const pushUserMsg = (text) =>
        setChatHistory(prev => [...prev, { type: CHAT_TYPE.USER, text }]);

    /** Turn one card from the server into the chat entry its component already expects. */
    const cardToMessage = (card) => {
        if (!card) return null;
        if (card.type === 'cost') {
            return { type: CHAT_TYPE.BOT, text: '', subType: 'confirm_build', costInfo: card.data };
        }
        if (card.type === 'insufficient_credits') {
            // The shortfall and who can act on it (card.data.payer) drive the card's actions.
            return { type: CHAT_TYPE.BOT, text: '', subType: 'insufficient_credits', shortfallInfo: card.data || null };
        }
        if (card.type === 'build_failed') {
            return { type: CHAT_TYPE.BOT, text: '', subType: 'build_failed', phase: card.phase || card.data?.phase || 'outline' };
        }
        return null;
    };

    const applyTurn = (data) => {
        if (!data) return;

        // The server sends the same params object the build will use, so the wizard is
        // never reconstructed from the reply text.
        if (data.params) setWizard(prev => ({ ...prev, ...data.params }));

        /*
         * The step the server says it is on. The only authoritative statement
         * of progress — the reply prose varies run to run, and the wizard only
         * fills in AFTER an answer is given, so both lag by a turn.
         */
        if (data.stage) setCurationStage(String(data.stage));

        const messages = [];
        /*
         * reply_options: the choices this reply offers, from the server (null for a
         * typed question). Kept on the message so a restored transcript still has them.
         */
        if (data.reply_markdown) {
            messages.push({
                type: CHAT_TYPE.BOT,
                text: data.reply_markdown,
                ...(data.reply_options ? { replyOptions: data.reply_options } : {}),
            });
        }

        const card = (dropCostCardOnceRef.current && data.card?.type === 'cost') ? null : data.card;
        dropCostCardOnceRef.current = false;
        confirmBuildCostRef.current = card?.type === 'cost' ? card.data : null;
        const cardMsg = cardToMessage(card);
        if (cardMsg) messages.push(cardMsg);

        const insufficient = data.card?.type === 'insufficient_credits';
        insufficientCreditsRef.current = insufficient;
        setInsufficientCredits(insufficient);
        programBuildFailedRef.current = data.card?.type === 'build_failed';

        if (messages.length) setChatHistory(prev => [...prev, ...messages]);

        /*
         * The server states a started build three ways in one response: the
         * build_started card, done (which is Boolean(program_code)), and
         * program_code itself. Any of them is the same fact, so take the
         * union — a build left running server-side while the page still
         * shows the cost card is the worst outcome here, and the second
         * Proceed only earns "your program is already being built".
         *
         * program_code is still required: only the branch that actually ran
         * the build pipeline sets it, so an ordinary interview turn can
         * never reach this, and the poller needs it regardless.
         */
        const serverStartedBuild = data.card?.type === 'build_started' || data.done === true;
        if (serverStartedBuild && data.program_code) {
            serverBuildInitiatedRef.current = true;
            serverProgramCodeRef.current    = data.program_code;
            buildTriggeredRef.current       = true;
            setBuildStarted(true);
            handleBuildProgram(data.program_code);
        }
    };

    /**
     * @param {boolean} skipPushUser suppress the user bubble (button presses add their own,
     *                               and the landing page has already shown the message)
     */
    const sendTurn = async (programId, user_message, skipPushUser = false) => {
        if (!programId || inFlightRef.current) return { buildTriggered: false };
        if (user_message && !skipPushUser) pushUserMsg(user_message);
        inFlightRef.current = true;
        setIsBotThinking(true);
        try {
            const res = await CurationPromptV2Service(programId, user_message);
            applyTurn(res?.data);
            return { buildTriggered: buildTriggeredRef.current };
        } catch (err) {
            // invokeApi has already surfaced the failure; keep the transcript honest rather
            // than leaving the educator looking at a spinner that stopped for no reason.
            console.error('[useDqAiProgramChat] turn failed:', err);
            setChatHistory(prev => [...prev, {
                type: CHAT_TYPE.BOT,
                text: "Sorry, I couldn't reach the assistant just then. Please send that again.",
            }]);
            return { buildTriggered: false };
        } finally {
            inFlightRef.current = false;
            setIsBotThinking(false);
        }
    };

    /**
     * The chat's program draft, created on the first message. A draft cannot be created
     * when the builder's pool cannot fund a single session: that refusal stays in the
     * chat as a shortfall card — what is short and who can act on it — instead of only
     * a notice that disappears, and the message is kept to send again.
     *
     * @param {string}  message       the first message, kept if the chat cannot start
     * @param {boolean} messageShown  whether the transcript already shows the message
     */
    const ensureProgramId = async (message, messageShown) => {
        if (programIdState) return programIdState;
        try {
            const res = await createInsiderProgramV4Service();
            const id = res?.data?.program_id;
            setProgramIdState(id);
            pendingStartRef.current = null;
            return id;
        } catch (err) {
            const data = err?.body?.data;
            if (data?.reason === 'insufficient_credits' || data?.reason === 'credits_unavailable') {
                pendingStartRef.current = message;
                setChatHistory(prev => [
                    ...prev,
                    ...(messageShown ? [] : [{ type: CHAT_TYPE.USER, text: message }]),
                    { type: CHAT_TYPE.BOT, text: err.message },
                    {
                        type: CHAT_TYPE.BOT, text: '', subType: 'insufficient_credits',
                        shortfallInfo: data.reason === 'credits_unavailable' ? { unavailable: err.message } : data,
                    },
                ]);
            }
            return null;
        }
    };

    const sendMessage = async (user_message) => {
        if (!user_message?.trim()) return;
        const id = await ensureProgramId(user_message, false);
        if (id) await sendTurn(id, user_message);
    };

    /** "Proceed" on the cost card — the sentinel the server acts on directly. */
    const confirmBuildCost = async () => {
        if (!programIdState) return;
        confirmBuildCostRef.current = null;
        pushUserMsg("Yes, let's proceed.");
        /*
         * The Building step ticks here, before the server has answered.
         *
         * It marks the persons progress through the wizard, and pressing
         * Proceed is where that ends — every answer is in and the cost is
         * agreed. A build that fails to start still leaves this step behind
         * them; the failure notice and Try Again are what speak to that.
         *
         * State only, not buildTriggeredRef: the ref drives resumeBuild on a
         * restore, and an attempt that never started has no build to resume.
         */
        setBuildStarted(true);
        await sendTurn(programIdState, 'confirm build', true);
    };

    /**
     * "No, make changes" on the cost card: a real change request to the chatbot, which
     * asks what should change and keeps the interview open until the next summary.
     */
    const declineBuildCost = async () => {
        confirmBuildCostRef.current = null;
        if (!programIdState) return;
        // The chatbot handles the change request itself ("What should change - the topic,
        // the audience, ...?"), and the transcript records it; its reply carries no card.
        dropCostCardOnceRef.current = true;
        await sendTurn(programIdState, 'Change my answers');
    };

    /**
     * Pick up where the shortfall stopped the chat: after a successful top-up, or when
     * someone who cannot buy presses Try again once credits have been added. A chat that
     * could not start sends its first message again; otherwise the server is asked to
     * build again ('try now'), and answers with another shortfall card if still short.
     *
     * @param {string} reply  the person's side of it, shown in the transcript
     */
    const retryAfterTopUp = async (reply = "I've added credits. Let's continue.") => {
        if (!programIdState) {
            const pending = pendingStartRef.current;
            if (!pending) return;
            pushUserMsg(reply);
            const id = await ensureProgramId(pending, true);
            if (id) await sendTurn(id, pending, true);
            return;
        }
        insufficientCreditsRef.current = false;
        setInsufficientCredits(false);
        pushUserMsg(reply);
        await sendTurn(programIdState, 'try now', true);
    };

    /** Try again, from someone who cannot buy and has asked whoever can. */
    const retryAfterCredits = () => retryAfterTopUp('Try again.');

    const retryBuild = async () => {
        if (!programIdState) return;
        insufficientCreditsRef.current = false;
        setInsufficientCredits(false);
        programBuildFailedRef.current = false;
        setChatHistory(prev => prev.filter(m => m.subType !== 'build_failed'));
        await sendTurn(programIdState, 'retry build', true);
    };

    /** A user who signed up from the landing page arrives with their first message. */
    const startFromLanding = async (message) => {
        if (!message?.trim()) return;
        setChatHistory([{ type: CHAT_TYPE.USER, text: message.trim() }]);
        const id = await ensureProgramId(message.trim(), true);
        if (id) await sendTurn(id, message.trim(), true);
    };

    /** Restoring an in-progress program: adopt its id without starting a turn. */
    const initWithExistingProgram = (id) => {
        setProgramIdState(id);
    };

    /**
     * What survives a reload. The conversation itself lives on the server now, so this is
     * only the UI's own build bookkeeping. Field names match what the page already
     * persists, so older saved states restore without a migration.
     */
    const getCurationState = () => ({
        /*
         * Saved so a restored conversation knows which step it is on.
         *
         * curationStage is otherwise only set by a turn coming back, so
         * Continue-where-you-left-off started with it empty — and every
         * stage-based option disappeared, giving the same conversation
         * different chips depending on how it was opened.
         */
        curationStage,
        confirmBuildCost:     confirmBuildCostRef.current,
        buildTriggered:       buildTriggeredRef.current,
        serverBuildInitiated: serverBuildInitiatedRef.current,
        serverProgramCode:    serverProgramCodeRef.current,
        insufficientCredits:  insufficientCreditsRef.current,
        programBuildFailed:   programBuildFailedRef.current,
    });

    const restoreCurationState = (state) => {
        if (!state) return;
        confirmBuildCostRef.current     = state.confirmBuildCost || null;
        buildTriggeredRef.current       = Boolean(state.buildTriggered);
        serverBuildInitiatedRef.current = Boolean(state.serverBuildInitiated);
        serverProgramCodeRef.current    = state.serverProgramCode || null;
        insufficientCreditsRef.current  = Boolean(state.insufficientCredits);
        setInsufficientCredits(Boolean(state.insufficientCredits));
        programBuildFailedRef.current   = Boolean(state.programBuildFailed);
        if (state.curationStage) setCurationStage(String(state.curationStage));
        // Coming back to a program whose build was already started: the
        // stepper must show the same thing it showed before the reload.
        setBuildStarted(Boolean(state.buildTriggered) || Boolean(state.serverBuildInitiated));
    };

    return {
        curationStage,
        buildStarted,
        chatHistory, setChatHistory, isBotThinking,
        sendMessage, startFromLanding, initWithExistingProgram,
        programIdState,
        insufficientCredits,
        confirmBuildCost,
        declineBuildCost,
        retryAfterTopUp,
        retryAfterCredits,
        retryBuild,
        getCurationState, restoreCurationState,
    };
};
